// Écran du super administrateur de la plateforme (D-080) : les 25 modules du cahier des charges avec
// leur fiche (ce que fait le module, comment le montrer, comptes et écrans), l'activation globale et le
// réglage par collectivité, avec confirmation et motif conservé au journal.
import type { EtatLivraison } from '@cc/contrats';
import { Bandeau, DialogueConfirmation, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { CLE_MODULES_PLATEFORME, modifierModuleCollectivite, modifierModulePlateforme, useModulesPlateforme } from './api';
import { FicheModulePlateforme, type CibleModification } from './FicheModulePlateforme';
import styles from './plateforme.module.css';

type Filtre = 'tous' | EtatLivraison;
const FILTRES: { id: Filtre; libelle: string }[] = [
  { id: 'tous', libelle: 'Tous' },
  { id: 'livre', libelle: 'Livrés' },
  { id: 'partiel', libelle: 'Partiels' },
  { id: 'prevu', libelle: 'Prévus' },
];

const EFFETS_PLATEFORME_OFF = 'Le module disparaît de la navigation de toutes les collectivités et ses nouvelles créations sont refusées partout, quel que soit le réglage local. L’existant reste consultable.';
const EFFETS_PLATEFORME_ON = 'Le module redevient disponible ; chaque collectivité retrouve son réglage local (actif ou non).';
const EFFETS_LOCAL_OFF = 'Dans cette collectivité, les écrans du module disparaissent de la navigation et les nouvelles créations sont bloquées ; l’existant reste consultable.';
const EFFETS_LOCAL_ON = 'Dans cette collectivité, les écrans du module apparaissent pour les agents qui ont les droits correspondants et ses indicateurs sont calculés.';

export function PageModulesPlateforme() {
  const { contexte } = useSession();
  const modules = useModulesPlateforme();
  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [cible, setCible] = useState<CibleModification | null>(null);
  const [motif, setMotif] = useState('');
  const plateforme = contexte?.appartenance_active?.institution === 'plateforme';
  const modification = useAction({
    executer: (c: CibleModification) =>
      c.collectivite
        ? modifierModuleCollectivite(c.module.module, c.collectivite.id, { actif: c.actif, motif })
        : modifierModulePlateforme(c.module.module, { actif: c.actif, motif }),
    succes: (r) => `Module ${r.module} ${cible?.actif ? 'activé' : 'désactivé'}${cible?.collectivite ? ` pour ${cible.collectivite.nom}` : ' sur la plateforme'}.`,
    invalider: [CLE_MODULES_PLATEFORME, ['contexte'], ['modules'], ['audit']],
    apres: () => {
      setCible(null);
      setMotif('');
    },
  });

  if (!plateforme) return <Bandeau type="erreur">Cet écran est réservé au super administrateur de la plateforme.</Bandeau>;
  const tous = modules.data?.elements ?? [];
  const elements = tous.filter((m) => filtre === 'tous' || m.fiche.etat === filtre);
  const actifs = tous.filter((m) => m.actif_plateforme).length;
  const livres = tous.filter((m) => m.fiche.etat === 'livre').length;
  return (
    <>
      <EnTetePage
        etiquette="Plateforme"
        titre="Modules"
        description={
          modules.data
            ? `${actifs} module${actifs > 1 ? 's' : ''} actif${actifs > 1 ? 's' : ''} sur 25 sur la plateforme, ${livres} livrés. Un module désactivé ici disparaît de toutes les collectivités ; chaque fiche dit ce que fait le module et comment le montrer.`
            : undefined
        }
      />
      <div className={styles.filtres} role="group" aria-label="Filtrer par état de livraison">
        {FILTRES.map((f) => (
          <button key={f.id} type="button" className={styles.filtre} aria-pressed={filtre === f.id} onClick={() => setFiltre(f.id)}>
            {f.libelle}
          </button>
        ))}
      </div>
      <EtatRequete chargement={modules.isPending} erreur={modules.error} onReessayer={() => void modules.refetch()} fonction="Le catalogue des modules">
        <ul className={styles.liste} aria-label="Modules de la plateforme">
          {elements.map((m) => (
            <li key={m.module}>
              <FicheModulePlateforme module={m} collectivites={modules.data?.collectivites ?? []} onModifier={(c) => setCible(c)} />
            </li>
          ))}
        </ul>
      </EtatRequete>
      <DialogueConfirmation
        ouvert={cible !== null}
        titre={cible ? `${cible.actif ? 'Activer' : 'Désactiver'} ${cible.module.module}${cible.collectivite ? ` pour ${cible.collectivite.nom}` : ' sur la plateforme'}` : ''}
        message={
          cible ? (
            <>
              <p>
                <strong>{cible.module.libelle}</strong>
              </p>
              <p>{cible.collectivite ? (cible.actif ? EFFETS_LOCAL_ON : EFFETS_LOCAL_OFF) : cible.actif ? EFFETS_PLATEFORME_ON : EFFETS_PLATEFORME_OFF}</p>
            </>
          ) : null
        }
        libelleConfirmer={cible?.actif ? 'Activer le module' : 'Désactiver le module'}
        variante={cible?.actif ? 'principal' : 'danger'}
        enCours={modification.enCours}
        confirmationDesactivee={motif.trim().length < 3}
        onConfirmer={() => cible && modification.lancer(cible)}
        onAnnuler={() => {
          setCible(null);
          setMotif('');
          modification.reinitialiser();
        }}
      >
        <ZoneTexte libelle="Motif" aide="Trois caractères au moins ; conservé au journal" value={motif} onChange={(e) => setMotif(e.target.value)} required />
        {modification.messageErreur ? <Bandeau type="erreur">{modification.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}
